import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Banner, Button, Input } from '@dd/ui';
import { api, errText } from '@/lib/api';
import { useAuth, type Admin } from '@/lib/auth';

interface LoginRes { step: 'enroll' | 'totp'; challenge: string; enroll?: { secret: string; otpauth: string } }

export function Login() {
  const [email, setEmail] = useState(''); const [password, setPassword] = useState('');
  const [step, setStep] = useState<LoginRes | null>(null);
  const [code, setCode] = useState(''); const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    if (step?.enroll) void QRCode.toDataURL(step.enroll.otpauth, { margin: 1, width: 220 }).then(setQr).catch(() => setQr(null));
  }, [step]);

  const submitPw = async () => {
    setBusy(true); setErr(null);
    try { setStep(await api.post<LoginRes>('/auth/login', { email: email.trim(), password })); setCode(''); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const submitCode = async () => {
    if (!step) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.post<{ admin: Admin; csrfToken: string }>('/auth/totp', { challenge: step.challenge, code });
      useAuth.getState().signedIn(r.admin, r.csrfToken);
    } catch (e) { setErr(errText(e)); setCode(''); } finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-[#12141B] p-12 text-white lg:flex">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand/25 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-brand/10 blur-3xl" />
        <div className="relative flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand text-xl font-black shadow-lg shadow-brand/40">D</span><div><div className="text-lg font-extrabold">DokaanDigit</div><div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/45">NTechEdge · Operations</div></div></div>
        <div className="relative max-w-md">
          <h2 className="text-4xl font-extrabold leading-tight tracking-tight">The control room for Bangladesh’s smartest shop app.</h2>
          <p className="mt-4 text-white/60">Customers, payments, plans and support — one place, every action audited by name.</p>
        </div>
        <div className="relative text-xs text-white/40">DokaanDigit is a flagship product of NTechEdge.</div>
      </aside>
      <main className="flex items-center justify-center bg-[#F3F4F7] p-6">
        <div className="w-full max-w-md rounded-2xl border border-line/80 bg-white p-7 shadow-[0_10px_40px_rgb(16_24_40/0.08)]">
          <div className="mb-6"><h1 className="text-2xl font-extrabold tracking-tight">Admin sign-in</h1><p className="mt-1 text-sm text-ink-3">Two-step verification is required for everyone.</p></div>
        {err && <Banner tone="bad" className="mb-4">{err}</Banner>}
        {!step ? (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void submitPw(); }}>
            <Input label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            <Input label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button type="submit" size="lg" block loading={busy} disabled={!email || !password}>Continue</Button>
            <p className="text-center text-xs text-ink-3">Access is limited to allow-listed networks. Every action is audited.</p>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void submitCode(); }}>
            {step.step === 'enroll' && step.enroll ? (
              <>
                <Banner tone="warn">Two-step verification is mandatory. Scan this code with an authenticator app (Google Authenticator, Authy, 1Password), then enter the 6-digit code.</Banner>
                <div className="flex flex-col items-center gap-2">
                  {qr ? <img src={qr} alt="TOTP QR code" width={220} height={220} className="rounded-xl border border-line" /> : <div className="h-[220px] w-[220px] rounded-xl bg-neutral-100" />}
                  <div className="text-xs text-ink-3">Can't scan? Enter this key manually:</div>
                  <code className="select-all break-all rounded-lg bg-neutral-100 px-3 py-1.5 text-sm font-semibold tracking-wider">{step.enroll.secret}</code>
                </div>
              </>
            ) : <p className="text-sm text-ink-2">Enter the 6-digit code from your authenticator app.</p>}
            <Input label="6-digit code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus className="[&_input]:text-center [&_input]:text-2xl [&_input]:tracking-[0.4em]" />
            <Button type="submit" size="lg" block loading={busy} disabled={code.length !== 6}>{step.step === 'enroll' ? 'Activate & sign in' : 'Sign in'}</Button>
            <Button variant="ghost" block onClick={() => { setStep(null); setErr(null); setPassword(''); }}>Back</Button>
          </form>
        )}
        </div>
      </main>
    </div>
  );
}
